import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Mail, Phone } from "lucide-react";
import { adminApi, formatError } from "@/lib/adminApi";
import { SectionHeader } from "@/pages/admin/ui";

const STATUS = [["yeni", "Yeni"], ["görüşüldü", "Görüşüldü"], ["kapandı", "Kapandı"]];
const fmt = (iso) => new Date(iso).toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" });
const EMAIL = { sent: "E-posta gönderildi", failed: "E-posta gönderilemedi", skipped: "Bildirim adresi yok" };

function RequestCard({ r, onSaved }) {
  const [status, setStatus] = useState(r.status);
  const [notes, setNotes] = useState(r.notes || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await adminApi.patch(`/admin/group-requests/${r.request_id}`, { status, notes });
      onSaved(data);
      toast.success("Talep güncellendi");
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className="rounded-2xl border border-line bg-white p-5 space-y-4" data-testid={`admin-request-${r.request_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xl font-serif font-semibold text-forest">{r.name}</p>
          <p className="text-xs text-ink-muted">{fmt(r.created_at)}</p>
        </div>
        <div className="flex flex-wrap gap-2 text-xs font-semibold">
          <span className={`rounded-full px-2.5 py-1 ${r.status === "yeni" ? "bg-[#FEF3C7] text-[#78350F]" : "bg-stone-100 text-stone-700"}`} data-testid={`admin-request-${r.request_id}-status-badge`}>{STATUS.find(([v]) => v === r.status)?.[1]}</span>
          <span className="rounded-full bg-stone-100 px-2.5 py-1 text-stone-700" data-testid={`admin-request-${r.request_id}-lang`}>{r.lang === "en" ? "EN" : "TR"}</span>
          {r.email_status?.state && <span className="inline-flex items-center gap-1 rounded-full bg-stone-100 px-2.5 py-1 text-stone-700"><Mail className="h-3 w-3" aria-hidden="true" />{EMAIL[r.email_status.state]}</span>}
        </div>
      </div>
      <dl className="grid gap-3 sm:grid-cols-3 text-sm">
        <div><dt className="text-ink-muted">Telefon</dt><dd><a href={`tel:${r.phone.replace(/[^\d+]/g, "")}`} className="inline-flex items-center gap-1 font-medium text-forest hover:underline"><Phone className="h-3.5 w-3.5" aria-hidden="true" />{r.phone}</a></dd></div>
        <div><dt className="text-ink-muted">Tercih edilen tarih</dt><dd className="font-medium">{r.date.split("-").reverse().join(".")}</dd></div>
        <div><dt className="text-ink-muted">Tahmini kişi sayısı</dt><dd className="font-medium">{r.people}</dd></div>
      </dl>
      {r.message && <p className="text-sm text-ink whitespace-pre-line rounded-xl bg-cream-2 p-3">{r.message}</p>}
      <div className="grid gap-3 sm:grid-cols-[200px_1fr_auto] sm:items-end">
        <label className="text-sm font-medium space-y-1">Durum
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full min-h-[44px] rounded-xl border border-line px-3 bg-white text-base" data-testid={`admin-request-${r.request_id}-status`}>
            {STATUS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label className="text-sm font-medium space-y-1">Notlar
          <textarea rows={1} value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full min-h-[44px] rounded-xl border border-line px-3 py-2.5 text-base" data-testid={`admin-request-${r.request_id}-notes`} />
        </label>
        <button type="button" onClick={save} disabled={busy} className="min-h-[44px] px-6 rounded-full bg-forest text-cream text-sm font-semibold disabled:opacity-60" data-testid={`admin-request-${r.request_id}-save`}>{busy ? "…" : "Güncelle"}</button>
      </div>
    </li>
  );
}

export default function RequestsSection() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    adminApi.get("/admin/group-requests").then(({ data }) => setItems(data)).catch((e) => toast.error(formatError(e)));
  }, []);

  const exportCsv = async () => {
    try {
      const { data } = await adminApi.get("/admin/group-requests.csv", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = Object.assign(document.createElement("a"), { href: url, download: "grup-talepleri.csv" });
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  return (
    <section data-testid="admin-section-talepler">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <SectionHeader title="Talepler" text="Grup ziyareti talepleri, en yeniden eskiye." />
        <button type="button" onClick={exportCsv} className="inline-flex items-center gap-2 min-h-[44px] px-5 rounded-full border border-line bg-white text-sm font-semibold hover:bg-cream" data-testid="admin-requests-export-csv">
          <Download className="h-4 w-4" aria-hidden="true" /> CSV indir
        </button>
      </div>
      {items === null && <p className="text-ink-muted">Yükleniyor…</p>}
      {items?.length === 0 && <p className="rounded-2xl border border-dashed border-line bg-white p-8 text-center text-ink-muted" data-testid="admin-requests-empty">Henüz talep yok.</p>}
      <ul className="space-y-4" data-testid="admin-requests-list">
        {items?.map((r) => <RequestCard key={r.request_id} r={r} onSaved={(n) => setItems(items.map((x) => (x.request_id === n.request_id ? n : x)))} />)}
      </ul>
    </section>
  );
}
